import React, { useMemo } from 'react'
import classNames from 'classnames'
import { useTranslation } from '../../../translations'
import { useContent } from '../../../redux/reducer/content'
import { useUser } from '../../../redux/reducer/user'
import { safeHTML } from '../../../utils/safeHTML'
import { buyBeautiesModal } from '../../../constants/copyright-modals'
import Icon from '../../baseComponents/gui/icon/Icon'
import CustomModal from '../../baseComponents/gui/customModal/CustomModal'
import AppLoader from '../../features/appLoader/AppLoader'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import { useModal } from '../../../hooks/useModal'
import { useSendProductToGoldenAppleStore } from '../../../hooks/useSendProductToGoldenAppleStore'
import { useLanguageControls } from '../../../hooks/useLanguageControls'
import { useFeatureToggle } from '../../../hooks/useFeatureToggle'
import { customizationContent } from '../../../constants/copyright'
import { useBuyBeautiesModalContent } from './model/hooks/useBuyBeautiesModalContent'
import { gtmFunctions } from '../../../constants/analytics/GTM'
import { getProductCurrentState } from '../../features/product/utils/getProductCurrentState'

const {
  buttons: { buy, elfBuy, favorites },
} = buyBeautiesModal

const BuyBeautiesModal = React.forwardRef(function BuyBeautiesModal(
  { itemId, productId, callbacks, showXp = true, filter },
  ref,
) {
  const {
    profile: { items, beauties, gacha_event_cash },
  } = useUser()
  const { t } = useTranslation()
  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')

  const { notFilteredShopItems } = useContent()
  const { getLocalizedCurrency } = useLanguageControls()
  const { addModal } = useModal()
  const { state } = useContent()

  const { title, beauty_routine_id, sku, link, description, new_price, hasFavourite, inGaStock } =
    useBuyBeautiesModalContent(productId)

  const { isCustomizationState, customizationFilterName } = getProductCurrentState(state, filter)

  const panelValue = gameTamagotchiBeauties
    ? {
        type: 'beauties',
        value: beauties,
      }
    : {
        type: 'gifts',
        value: gacha_event_cash,
      }

  const { preview_image, reward_score, beautiesCost, giftCost } = useMemo(
    () => notFilteredShopItems.find(({ id }) => id === itemId),
    [notFilteredShopItems, itemId],
  )

  // Проверка баланса
  const isCanBuy = gameTamagotchiBeauties
    ? beauties >= (beautiesCost || 0)
    : gacha_event_cash >= (giftCost || 0)

  // Состояние предмета у пользователя
  const necessaryItem = items?.find?.(({ id }) => id === itemId)
  const status = !necessaryItem ? 'absent' : necessaryItem?.is_equipped ? 'equipped' : 'unequipped'

  const { closeModal, id } = useModal()
  const close = () => {
    closeModal(id)
  }

  // Логика покупки/экипировки эльфу
  const elfBuyCallback = () => {
    ;({
      absent() {
        if (isCustomizationState) {
          gtmFunctions.GTMMoreBuyForElf({ block_name: customizationFilterName })
        }

        // Для обеих валют показываем подтверждение покупки перед списанием.
        addModal({
          type: 'buyModal',
          props: {
            itemData: { priceData: gameTamagotchiBeauties ? { beautiesCost } : { giftCost } },
            title: gameTamagotchiBeauties
              ? 'customization.buyBeautiesModal.title'
              : 'customization.buyGiftsModal.title',
            callbacks: { buy: () => callbacks.buyItem(itemId) },
            buttons: gameTamagotchiBeauties
              ? customizationContent.buyBeautiesModalButtons
              : customizationContent.buyGiftsModalButtons,
          },
        })
      },
      equipped() {},
      unequipped() {
        callbacks.equipItem(itemId)
      },
    })[status]?.()
  }

  const {
    favoriteCallback,
    basketCallback,
    hasBasket,
    isPending: isPendingStoreRequests,
  } = useSendProductToGoldenAppleStore({
    callbacks,
    beauty_routine_id,
    id: productId,
    sku,
    link,
    hasFavourite,
    filter,
  })

  const buttonCostProps = gameTamagotchiBeauties
    ? !!beautiesCost && {
        count: { absent: beautiesCost }[status],
        beauties: { absent: !!beautiesCost }[status],
      }
    : !!giftCost && {
        count: { absent: giftCost }[status],
        gifts: { absent: !!giftCost }[status],
      }

  const basketText = !hasBasket
    ? !new_price // Не добавлен в корзину
      ? t(buy.text.noCurrency) // Нету цены - текст без цены
      : `${t(buy.text.have, { cost: new_price })}${getLocalizedCurrency()}` // Цена есть - текст с ценой и знаком валюты
    : t(buy.text.dontHave) // Добавлен в корзину

  const showPanel = gameTamagotchiBeauties ? typeof beauties === 'number' : true

  return (
    <CustomModal>
      <div
        className={'buy-beauties-modal'}
        ref={ref}
      >
        <div className={'buy-beauties-modal__content'}>
          <CustomButton
            className={'buy-beauties-modal__close custom-button_cross'}
            icon={'buttons/cross'}
            onClick={close}
          />

          {showPanel && (
            <div className={'buy-beauties-modal__panel'}>
              <div
                key={`p${panelValue.type}`}
                className='buy-beauties-modal__panel-item'
              >
                <div className='buy-beauties-modal__panel-item-icon'>
                  <Icon name={`${panelValue.type}`} />
                </div>
                <div className='buy-beauties-modal__panel-item-value'>{panelValue.value}</div>
              </div>
            </div>
          )}

          <div className={'buy-beauties-modal__preview'}>
            <img
              src={preview_image}
              alt={'item-img'}
            />
          </div>
          <h2 className={'buy-beauties-modal__name'}>{safeHTML(title)}</h2>
          <p className={'buy-beauties-modal__description'}>{safeHTML(description)}</p>

          {showXp && !!reward_score && !necessaryItem && (
            <div className={'buy-beauties-modal__hp'}>
              <div className={'buy-beauties-modal__hp-title'}>{safeHTML(`+${reward_score}XP`)}</div>
              <div className={'buy-beauties-modal__hp-text'}>{safeHTML(t('other.toRating'))}</div>
            </div>
          )}

          <div
            className={classNames('buy-beauties-modal__buttons', { 'not-in-stock': !inGaStock })}
          >
            {inGaStock && (
              <>
                <CustomButton
                  {...buy.attr}
                  className={classNames(buy.attr.className)}
                  text={basketText}
                  disabled={hasBasket}
                  onClick={basketCallback}
                />
                <CustomButton
                  {...favorites.attr}
                  className={classNames(favorites.attr.className, {
                    'buy-beauties-modal__favorite_has-favorite': hasFavourite,
                  })}
                  disabled={hasFavourite}
                  onClick={favoriteCallback}
                />
              </>
            )}

            <CustomButton
              {...elfBuy.attr}
              className={classNames(elfBuy.attr.className, {
                'buy-beauties-modal__elf-buy_equipped': { equipped: true, absent: !isCanBuy }[
                  status
                ],
              })}
              text={elfBuy.text[status]}
              icon={elfBuy.icon[status]}
              {...buttonCostProps}
              disabled={{ equipped: true, absent: !isCanBuy }[status]}
              onClick={elfBuyCallback}
            />
          </div>
        </div>
        {isPendingStoreRequests && <AppLoader />}
      </div>
    </CustomModal>
  )
})

export default BuyBeautiesModal
BuyBeautiesModal.propTypes = {}
